import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 51扒瓜网`;
    }
  }, [category]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center px-4">
          <h1 className="font-heading text-2xl text-foreground-950 mb-4">分类不存在</h1>
          <Link to="/" className="text-primary-600 hover:underline">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Breadcrumb */}
      <nav aria-label="面包屑导航" className="pt-24 pb-4 px-4 md:px-8 lg:px-12">
        <ol className="flex items-center gap-2 text-sm text-foreground-500 max-w-7xl mx-auto">
          <li>
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          </li>
          <li aria-hidden="true"><i className="ri-arrow-right-s-line text-xs" /></li>
          <li aria-current="page" className="text-primary-600 font-medium">{category.name}</li>
        </ol>
      </nav>

      {/* Category Hero */}
      <section className="px-4 md:px-8 lg:px-12 pb-10 md:pb-14">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden bg-foreground-900 p-8 md:p-12 lg:p-16">
            <div className="absolute inset-0 opacity-20">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20newsroom%20atmosphere%20with%20multiple%20television%20screens%20showing%20entertainment%20news%2C%20warm%20red%20and%20amber%20lighting%2C%20dark%20moody%20background%2C%20cinematic%20editorial%20photography&width=1400&height=500&seq=category-hero&orientation=landscape"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-900 via-foreground-900/80 to-transparent" />
            <div className="relative z-10 max-w-2xl">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold text-background-50 mb-4 ${category.color}`}>
                {category.name}
              </span>
              <h1 className="font-heading font-black text-2xl sm:text-3xl md:text-4xl text-background-50 mb-4">
                {category.name}频道
              </h1>
              <p className="text-base md:text-lg text-background-300 leading-relaxed mb-6">
                {category.description}
              </p>
              <div className="flex items-center gap-4 text-sm text-background-400">
                <span className="flex items-center gap-1">
                  <i className="ri-article-line" />
                  {categoryArticles.length} 篇文章
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-fire-line" />
                  {(categoryArticles.reduce((sum, a) => sum + a.views, 0) / 10000).toFixed(0)}万+ 总阅读
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article List */}
      <section className="px-4 md:px-8 lg:px-12 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-bold text-xl text-foreground-950">
                  最新文章
                </h2>
                <span className="text-sm text-foreground-500">
                  共 {categoryArticles.length} 篇
                </span>
              </div>

              <div className="space-y-5">
                {categoryArticles.map((article, i) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col sm:flex-row gap-4 md:gap-5 p-4 md:p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/60 transition-all duration-300 animate-fade-in-up"
                    style={{ animationDelay: `${i * 0.08}s`, opacity: 0 }}
                  >
                    <div className="sm:w-48 md:w-56 h-40 sm:h-32 md:h-36 rounded-lg overflow-hidden shrink-0">
                      <img
                        src={article.image}
                        alt={article.title}
                        loading="lazy"
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                          {article.categoryName}
                        </span>
                        {article.isExclusive && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-50 text-accent-700">
                            独家
                          </span>
                        )}
                        {article.isHot && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
                            热门
                          </span>
                        )}
                      </div>
                      <h3 className="font-heading font-bold text-base md:text-lg text-foreground-950 leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2 hidden sm:block">
                        {article.summary}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-foreground-500">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line" />
                          {(article.views / 10000).toFixed(1)}万
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line" />
                          {article.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}

                {categoryArticles.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-foreground-500">该分类暂无文章</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <aside className="w-full md:w-72 shrink-0 space-y-6">
              {/* Other Categories */}
              <div className="bg-background-100 rounded-xl p-5">
                <h3 className="font-heading font-semibold text-base text-foreground-950 mb-4">
                  其他频道
                </h3>
                <div className="space-y-2">
                  {categories.filter((c) => c.id !== category.id).map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-background-50 transition-colors"
                    >
                      <span className="text-sm text-foreground-700">{cat.name}</span>
                      <span className="text-xs text-foreground-500">{cat.articleCount}篇</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Hot Articles */}
              <div className="bg-background-100 rounded-xl p-5">
                <h3 className="font-heading font-semibold text-base text-foreground-950 mb-4">
                  全站热门
                </h3>
                <div className="space-y-4">
                  {articles
                    .filter((a) => a.isHot && a.category !== slug)
                    .slice(0, 5)
                    .map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="group flex gap-3"
                      >
                        <div className="w-16 h-12 rounded-md overflow-hidden shrink-0">
                          <img
                            src={article.image}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {article.title}
                          </p>
                          <span className="text-xs text-foreground-500">{(article.views / 10000).toFixed(1)}万阅读</span>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* BreadcrumbList Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'http://rkldzswyxgs.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: category.name,
              item: `http://rkldzswyxgs.com/category/${category.slug}`,
            },
          ],
        })}
      </script>

      <Footer />
    </div>
  );
}